import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useMemo } from 'react';
import {
  TrendingUp, PenTool, Search, Target, Zap,
  UsersRound, Heart, BarChart3, Map, CreditCard, BookOpen, Settings,
} from 'lucide-react';
import { getCourseBySlug, type Lesson } from '@/lib/learning/courses';
import { GROWTH_101_LESSONS } from '@/lib/learning/growth-101';
import { CONTENT_MARKETING_LESSONS } from '@/lib/learning/content-marketing';
import { ROADMAP_PLANNING_LESSONS } from '@/lib/learning/roadmap-planning';
import { SEO_REFERENCEMENT_LESSONS } from '@/lib/learning/seo-referencement';
import { SALES_INTELLIGENCE_LESSONS } from '@/lib/learning/sales-intelligence';
import { MARKETING_AUTOMATION_LESSONS } from '@/lib/learning/marketing-automation';
import { REFERRAL_VIRALITY_LESSONS } from '@/lib/learning/referral-virality';
import { CUSTOMER_SUCCESS_LESSONS } from '@/lib/learning/customer-success';
import { DATA_ANALYTICS_LESSONS } from '@/lib/learning/data-analytics';
import { STRIPE_BILLING_V2_LESSONS } from '@/lib/learning/stripe-billing-v2';
import { AI_ROUTER_CONFIG_LESSONS } from '@/lib/learning/ai-router-config';
import { PLAN_GATES_MANAGEMENT_LESSONS } from '@/lib/learning/plan-gates-management';
import { useLearningProgress } from '@/hooks/useLearningProgress';
import { LessonContent } from './components/LessonContent';
import { LessonNavigation } from './components/LessonNavigation';
import type { FC } from 'react';

const LESSONS_MAP: Record<string, Lesson[]> = {
  'growth-101': GROWTH_101_LESSONS,
  'content-marketing': CONTENT_MARKETING_LESSONS,
  'roadmap-planning': ROADMAP_PLANNING_LESSONS,
  'seo-referencement': SEO_REFERENCEMENT_LESSONS,
  'sales-intelligence': SALES_INTELLIGENCE_LESSONS,
  'marketing-automation': MARKETING_AUTOMATION_LESSONS,
  'referral-virality': REFERRAL_VIRALITY_LESSONS,
  'customer-success': CUSTOMER_SUCCESS_LESSONS,
  'data-analytics': DATA_ANALYTICS_LESSONS,
  'stripe-billing-v2': STRIPE_BILLING_V2_LESSONS,
  'ai-router-config': AI_ROUTER_CONFIG_LESSONS,
  'plan-gates-management': PLAN_GATES_MANAGEMENT_LESSONS,
};

const ICON_MAP: Record<string, FC<{ className?: string }>> = {
  TrendingUp, PenTool, Search, Target, Zap,
  UsersRound, Heart, BarChart3, Map, CreditCard, BookOpen, Settings,
};

/**
 * Page d'une lecon — contenu structure avec navigation et toggle "lu".
 */
export default function LessonPage() {
  const { courseSlug = '', lessonSlug = '' } = useParams();
  const navigate = useNavigate();
  const course = getCourseBySlug(courseSlug);
  const lessons = LESSONS_MAP[courseSlug] ?? [];
  const { isLessonCompleted, completeLesson, uncompleteLesson } = useLearningProgress();

  const lessonIdx = useMemo(() => lessons.findIndex((l) => l.slug === lessonSlug), [lessons, lessonSlug]);
  const lesson = lessonIdx >= 0 ? lessons[lessonIdx] : null;

  const prevSlug = lessonIdx > 0 ? lessons[lessonIdx - 1].slug : null;
  const nextSlug = lessonIdx < lessons.length - 1 ? lessons[lessonIdx + 1].slug : null;

  const completed = isLessonCompleted(courseSlug, lessonSlug);

  if (!course || !lesson) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-12 text-center">
        <p className="text-slate-500 dark:text-slate-400">Lecon introuvable.</p>
        <button
          onClick={() => navigate(courseSlug ? `/learning/${courseSlug}` : '/learning')}
          className="mt-4 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          Retour au parcours
        </button>
      </div>
    );
  }

  const handleToggle = () => {
    if (completed) {
      uncompleteLesson(courseSlug, lessonSlug);
    } else {
      completeLesson(courseSlug, lessonSlug);
    }
  };

  const LinkedIcon = lesson.linkedModule?.icon ? ICON_MAP[lesson.linkedModule.icon] : null;

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      {/* Breadcrumb */}
      <button
        onClick={() => navigate(`/learning/${courseSlug}`)}
        className="mb-6 flex items-center gap-1.5 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {course.title}
      </button>

      {/* Lesson header */}
      <div className="mb-6">
        <p className="text-xs font-medium text-slate-400 dark:text-slate-500 mb-1">
          Lecon {lessonIdx + 1} sur {lessons.length}
        </p>
        <h1 className="text-xl font-bold text-slate-900 dark:text-white leading-tight">
          {lesson.title}
        </h1>
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
          Duree estimee : {lesson.duration}
        </p>
      </div>

      {/* Sections */}
      <LessonContent sections={lesson.sections} />

      {/* Linked module CTA */}
      {lesson.linkedModule && (
        <div className="mt-6 flex items-center justify-between rounded-xl border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 p-4">
          <div className="flex items-center gap-3">
            {LinkedIcon && <LinkedIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-white">
                Acceder au module
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {lesson.linkedModule.label}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate(lesson.linkedModule!.path)}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 dark:bg-blue-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
          >
            Ouvrir
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Navigation */}
      <div className="mt-8">
        <LessonNavigation
          courseSlug={courseSlug}
          prevSlug={prevSlug}
          nextSlug={nextSlug}
          isCompleted={completed}
          onToggleComplete={handleToggle}
        />
      </div>
    </div>
  );
}
